import 'react-app-polyfill/ie9'
import 'core-js/actual';

import { SkyAuthProvider } from 'context/AuthContext'
import * as fbq from 'lib/fbpixels'
import { NextPage } from 'next'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Script from 'next/script'
import {
    ReactElement,
    ReactNode,
    useEffect,
} from 'react'
import SkyContext, { SkyAppDataProvider } from '../context/AppContext'
import '../styles/globals.scss'
import BookingPageContainer from 'context/bookingContext/BookingPageContainer'
import { TenantContextProvider } from 'context/TenantContext'
import { AppErrorProvider } from 'context/AppErrorContext'
import GlobalError from 'components/Modals/GlobalError'
import {ErrorBoundary} from 'react-error-boundary'
import ErrorFallback from 'components/ErrorBoundary'
import StripeIntegration from 'components/StripeIntegration'

type NextPageWithLayout = NextPage & {
    getLayout?(page: ReactElement): ReactNode
}

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout
}

const SpinnerImage = dynamic(() => import('components/Spinners/spinner-image'), {
    ssr: false,
})

export function SafeHydrate({ children }: Record<string, ReactElement>) {
    return (
        <div suppressHydrationWarning>
            {typeof window === 'undefined' ? null : children}
        </div>
    )
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
    SkyContext.displayName = 'AppContext'
    const getLayout = Component.getLayout ?? ((page) => page)
    const router = useRouter()
    useEffect(() => {
        // Get query parameters from the URL
        const { scheduling } = router.query;
    
        // Dynamically add class to the body based on the URL parameter
        if (scheduling) {
          document.body.classList.add(`iframe-link`);
        } 
      }, [router.query]);

    useEffect(() => {
        fbq.pageview()
        const handleRouteChange = () => {
            fbq.pageview()
        }
        router.events.on('routeChangeComplete', handleRouteChange)
        return () => {
            router.events.off('routeChangeComplete', handleRouteChange)
        }
    }, [router.events])

    useEffect(() => {
        if (router.pathname.includes('/_error'))
            router.push('/dashboard/calendar')
    }, [router.pathname])

    return (
        <SafeHydrate>
            <ErrorBoundary 
                FallbackComponent={ErrorFallback}
                onReset={() => {
                    window.location.reload()
                }}
            >
                <AppErrorProvider>
                    <SkyAppDataProvider>
                        <Script
                            id="fb-pixel-script"
                            strategy="afterInteractive"
                            dangerouslySetInnerHTML={{
                                __html: `
                            !function(f,b,e,v,n,t,s)
                            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                            n.queue=[];t=b.createElement(e);t.async=!0;
                            t.src=v;s=b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t,s)}(window, document,'script',
                            'https://connect.facebook.net/en_US/fbevents.js');
                            fbq('init', ${fbq.FACEBOOK_PIXEL_ID});
                        `,
                            }}
                        />
                        <TenantContextProvider>
                            <SkyAuthProvider>
                                <StripeIntegration>
                                    <BookingPageContainer>
                                        {getLayout(
                                            <Component
                                                {...pageProps}
                                            />
                                        )}
                                    </BookingPageContainer>
                                </StripeIntegration>
                            </SkyAuthProvider>
                        </TenantContextProvider>

                        <GlobalError />

                        <div id="global-block-loader" className='hidden'>
                            <div className={`absolute inset-0 flex flex-col justify-center items-center bg-gray-900/30`} style={{ zIndex: 999 }}>
                                <SpinnerImage />
                            </div>
                        </div>

                        <div id="global-loader" className='hidden'>
                            <div className={`fixed top-1/2 left-1/2 -translate-x-8 -translate-y-8`} style={{ zIndex: 998 }}>
                                <SpinnerImage />
                            </div>
                        </div>

                    </SkyAppDataProvider>
                </AppErrorProvider>
            </ErrorBoundary>
        </SafeHydrate>
    )
}

export default MyApp
